import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Loader2, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoalieCard = ({ team, teamName, goalie }) => (
  <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/30" data-testid={`goalie-card-${goalie.name}`}>
    <div className="flex flex-wrap items-center justify-between gap-1.5 border-b border-slate-800 bg-gradient-to-r from-lime-950/20 to-slate-900/20 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="font-display text-[14px] font-bold uppercase tracking-wide text-lime-300">{goalie.name}</span>
        <span className="rounded bg-slate-800 px-1.5 font-mono text-[9px] font-semibold text-slate-300">{team}</span>
      </div>
      <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{goalie.games} GP · {goalie.line_data_available ? "LINE DATA ✓" : "POSITION ONLY"}</span>
    </div>
    <div className="grid grid-cols-1 gap-2 p-2.5 sm:grid-cols-2">
      {/* position splits */}
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <div className="bg-slate-900/80 px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">Position Origin</div>
        <table className="w-full min-w-[280px] border-collapse">
          <thead>
            <tr className="bg-slate-900/50 font-mono text-[9px] uppercase tracking-wider text-slate-500">
              <th className="px-2 py-1 text-left">Pos</th>
              <th className="px-2 py-1 text-right">GA</th>
              <th className="px-2 py-1 text-right">Gm</th>
              <th className="px-2 py-1 text-right">G/G</th>
              <th className="px-2 py-1 text-right">1st</th>
              <th className="px-2 py-1 text-right">Multi</th>
            </tr>
          </thead>
          <tbody>
            {goalie.positions.map((p) => {
              const top = p.ga === Math.max(...goalie.positions.map((q) => q.ga));
              return (
                <tr key={p.pos} className="border-t border-slate-800/60">
                  <td className={`px-2 py-1 font-mono text-[11px] font-bold ${top ? "text-rose-300" : "text-slate-300"}`}>{p.pos}</td>
                  <td className={`px-2 py-1 text-right font-mono text-[11px] tabular-nums ${top ? "font-bold text-rose-300" : "text-amber-300"}`}>{p.ga}</td>
                  <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-400 tabular-nums">{p.games}</td>
                  <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-300 tabular-nums">{p.gpg}</td>
                  <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-300 tabular-nums">{p.first}</td>
                  <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-300 tabular-nums">{p.multi}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* line origin */}
      <div className="overflow-hidden rounded-lg border border-slate-800">
        <div className="bg-slate-900/80 px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">Exact Line Origin</div>
        {goalie.lines.length ? (
          <div className="max-h-[220px] overflow-auto">
            <table className="w-full min-w-[280px] border-collapse">
              <thead>
                <tr className="bg-slate-900/50 font-mono text-[9px] uppercase tracking-wider text-slate-500">
                  <th className="px-2 py-1 text-left">Line</th>
                  <th className="px-2 py-1 text-right">G</th>
                  <th className="px-2 py-1 text-right">Gm</th>
                  <th className="px-2 py-1 text-right">G/G</th>
                  <th className="px-2 py-1 text-right">1st</th>
                  <th className="px-2 py-1 text-right">Multi</th>
                </tr>
              </thead>
              <tbody>
                {goalie.lines.map((l) => (
                  <tr key={l.slot} className="border-t border-slate-800/60">
                    <td className="px-2 py-1 font-mono text-[11px] font-bold text-cyan-300">{l.slot}</td>
                    <td className="px-2 py-1 text-right font-mono text-[11px] font-bold text-lime-300 tabular-nums">{l.goals}</td>
                    <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-400 tabular-nums">{l.games}</td>
                    <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-300 tabular-nums">{l.gpg}</td>
                    <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-300 tabular-nums">{l.first}</td>
                    <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-300 tabular-nums">{l.multi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-6 text-center font-mono text-[11px] text-slate-600">INSUFFICIENT DATA</p>
        )}
      </div>
    </div>
  </div>
);

export default function GoalieOriginTab({ apiDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [teamF, setTeamF] = useState("ALL");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = apiDate ? { date: apiDate } : {};
      const res = await axios.get(`${API}/goalie-origin`, { params, timeout: 240000 });
      setData(res.data);
    } catch (e) { console.error(e); setError(true); }
    finally { setLoading(false); }
  }, [apiDate]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data?.teams) return [];
    const out = [];
    data.teams.forEach((t) => {
      if (teamF !== "ALL" && t.team !== teamF) return;
      t.goalies.forEach((g) => {
        if (q.trim() && !g.name.toLowerCase().includes(q.trim().toLowerCase())) return;
        out.push({ team: t.team, teamName: t.team_name, goalie: g });
      });
    });
    return out;
  }, [data, teamF, q]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="goalie-loading">
      <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      <p className="mt-4 font-mono text-xs uppercase tracking-widest">Loading goalie origin…</p>
    </div>
  );
  if (error || !data) return (
    <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="goalie-error">
      <p className="font-mono text-xs uppercase tracking-widest text-rose-400">Goalie origin unavailable</p>
      <button onClick={load} className="mt-3 rounded bg-slate-800 px-3 py-1 font-mono text-[11px] text-slate-300 hover:bg-slate-700">Retry</button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="goalie-origin-dashboard">
      <div className="rounded-xl border border-lime-900/40 bg-gradient-to-r from-lime-950/20 to-slate-900/20 p-4">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase tracking-wide text-lime-300">🥅 Goalie Origin Engine</h2>
        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-400">Where each goalie historically allows goals from — by position and exact line</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
          <select data-testid="goalie-team-filter" value={teamF} onChange={(e) => setTeamF(e.target.value)} className="bg-transparent font-mono text-[10px] uppercase text-slate-300 focus:outline-none">
            <option value="ALL">All Teams</option>
            {data.teams.map((t) => <option key={t.team} value={t.team}>{t.team}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
          <Search className="h-3.5 w-3.5 text-cyan-500" />
          <input data-testid="goalie-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search goalie…" className="w-32 bg-transparent font-mono text-[10px] text-slate-200 placeholder:text-slate-600 focus:outline-none" />
        </div>
      </div>
      <div className="space-y-3" data-testid="goalie-list">
        {rows.length === 0 ? (
          <p className="py-12 text-center font-mono text-[11px] uppercase tracking-widest text-slate-600">No goalies match</p>
        ) : rows.map((r, i) => <GoalieCard key={`${r.team}-${r.goalie.name}-${i}`} {...r} />)}
      </div>
    </div>
  );
}
